import { Box, CircularProgress, useMediaQuery } from "@mui/material";
import { isEmpty } from "lodash";
import { PropsWithChildren, Suspense, useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import Navigator from "../../components/Navigator";
import useAuth from "../../contexts/AuthContext";
import useUserContext from "../../contexts/UserContext";
import { useOnBehalfOfValue } from "../../recoil/admin/hooks";
import theme, { BACKGROUND_COLOR } from "../../styles/Theme";
import DashboardHeader from "./DashboardHeader";

const DRAWER_WIDTH = 256;

function DashboardLayout({ children }: PropsWithChildren<{}>) {
	const [mobileOpen, setMobileOpen] = useState(false);
	const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
	const { isAuthenticated, currentUserClaims, dashboardRedirect, clearDashboardRedirect } = useAuth();
	const { user, isLoading: userIsLoading } = useUserContext();
	const location = useLocation();

	// Claim the redirect once and keep the layout - and the index redirects below it - out of
	// the tree until the navigation has landed. Clearing it on mount instead let
	// /account -> buoys -> all run and override the pending navigation.
	const [pendingRedirect, setPendingRedirect] = useState<{ to: string; from: string } | null>(null);

	useEffect(() => {
		if (!dashboardRedirect) return;
		setPendingRedirect({ to: dashboardRedirect, from: location.pathname });
		clearDashboardRedirect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dashboardRedirect]);

	useEffect(() => {
		if (pendingRedirect && location.pathname !== pendingRedirect.from) {
			setPendingRedirect(null);
		}
	}, [location.pathname, pendingRedirect]);

	const handleDrawerToggle = () => {
		setMobileOpen(!mobileOpen);
	};

	if (isAuthenticated && (!currentUserClaims?.accountId || (!userIsLoading && !user?.accounts.length))) {
		return <Navigate to="/accounts" replace={true} state={location.state} />;
	}

	if (pendingRedirect && location.pathname === pendingRedirect.from) {
		return <Navigate to={pendingRedirect.to} replace={true} />;
	}

	return (
		<>
			<Box component="nav" sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}>
				{isSmUp ? null : (
					<Navigator
						PaperProps={{ style: { width: DRAWER_WIDTH, minWidth: "80%" } }}
						variant="temporary"
						open={mobileOpen}
						onClose={handleDrawerToggle}
					/>
				)}
				<Navigator
					PaperProps={{ style: { width: DRAWER_WIDTH } }}
					sx={{ display: { sm: "block", xs: "none" } }}
				/>
			</Box>
			<Box
				sx={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					bgcolor: BACKGROUND_COLOR,
				}}
			>
				<DashboardHeader onDrawerToggle={handleDrawerToggle} />
				<Suspense fallback={<CircularProgress />}>
					<Outlet />
					{children}
				</Suspense>
			</Box>
		</>
	);
}

function SafeDashboardLayout({ children, redirectPath }: PropsWithChildren<{ redirectPath?: string }>) {
	const { token, isAuthenticated, isLoadingAutenticated } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const { logout } = useAuth();
	const onBehalfOf = useOnBehalfOfValue();

	useEffect(() => {
		if (isAuthenticated || isLoadingAutenticated) {
			return;
		}

		navigate("/login", {
			state: {
				from: location,
			},
		});
	}, [isAuthenticated, isLoadingAutenticated]);

	async function logoutOnTabClose() {
		await logout();
	}

	useEffect(() => {
		const handleTabClose = (event: any) => {
			logoutOnTabClose();
			return;
		};
		if (onBehalfOf) {
			window.addEventListener("beforeunload", handleTabClose);
		}
		return () => {
			window.removeEventListener("beforeunload", handleTabClose);
		};
	}, []);

	return (
		<>
			{!isEmpty(token) ? (
				<DashboardLayout>{children}</DashboardLayout>
			) : (
				<Navigate
					to={redirectPath ?? "/login"}
					replace
					state={{
						from: location,
					}}
				/>
			)}
		</>
	);
}

export default SafeDashboardLayout;
